import { StockSymbol } from '@benzinga/session';

type InFlightEntry<TResult> = {
  promise: Promise<TResult>;
  symbolSet: Set<string>;
};

type PendingSubscriber<TResult> = {
  reject: (error: unknown) => void;
  resolve: (result: TResult) => void;
  symbols: StockSymbol[];
};

type PendingBatch<TResult> = {
  flushScheduled: boolean;
  subscribers: PendingSubscriber<TResult>[];
  symbols: Set<StockSymbol>;
};

const symbolsKey = (symbols: StockSymbol[]): string => [...symbols].sort().join(',');

const findCoveringInFlight = <TResult>(
  requested: StockSymbol[],
  inFlight: Iterable<InFlightEntry<TResult>>,
): InFlightEntry<TResult> | undefined => {
  for (const entry of inFlight) {
    if (requested.every(symbol => entry.symbolSet.has(symbol))) {
      return entry;
    }
  }
  return undefined;
};

/**
 * Coalesces symbol-keyed fetches: wait on an in-flight superset, or union
 * same-microtask callers into one fetch and slice per waiter.
 */
export const createSymbolRequestCoalescer = <TResult>(options: {
  fetch: (symbols: StockSymbol[]) => Promise<TResult>;
  scheduleFlush?: (flush: () => void) => void;
  slice: (result: TResult, symbols: StockSymbol[]) => TResult;
}): { request: (symbols: StockSymbol[]) => Promise<TResult> } => {
  const inFlight = new Map<string, InFlightEntry<TResult>>();
  let pending: PendingBatch<TResult> | null = null;
  const scheduleFlush = options.scheduleFlush ?? ((flush: () => void) => queueMicrotask(flush));

  const startFetch = (symbols: StockSymbol[]): InFlightEntry<TResult> => {
    const key = symbolsKey(symbols);
    const existing = inFlight.get(key);
    if (existing) return existing;

    const promise = options.fetch(symbols).finally(() => {
      if (inFlight.get(key)?.promise === promise) {
        inFlight.delete(key);
      }
    });

    const entry: InFlightEntry<TResult> = {
      promise,
      symbolSet: new Set(symbols),
    };
    inFlight.set(key, entry);
    return entry;
  };

  const flushPending = (): void => {
    const batch = pending;
    pending = null;
    if (!batch?.subscribers.length) return;

    const unionSymbols = [...batch.symbols];
    const entry = findCoveringInFlight(unionSymbols, inFlight.values()) ?? startFetch(unionSymbols);

    for (const subscriber of batch.subscribers) {
      entry.promise
        .then(result => options.slice(result, subscriber.symbols))
        .then(subscriber.resolve, subscriber.reject);
    }
  };

  const request = (symbols: StockSymbol[]): Promise<TResult> => {
    const uniqueSymbols = [...new Set(symbols)];
    if (uniqueSymbols.length === 0) {
      return options.fetch([]);
    }

    const covering = findCoveringInFlight(uniqueSymbols, inFlight.values());
    if (covering) {
      return covering.promise.then(result => options.slice(result, uniqueSymbols));
    }

    const batch = pending ?? {
      flushScheduled: false,
      subscribers: [] as PendingSubscriber<TResult>[],
      symbols: new Set<StockSymbol>(),
    };
    pending = batch;

    for (const symbol of uniqueSymbols) {
      batch.symbols.add(symbol);
    }

    const waiter = new Promise<TResult>((resolve, reject) => {
      batch.subscribers.push({ reject, resolve, symbols: uniqueSymbols });
    });

    if (!batch.flushScheduled) {
      batch.flushScheduled = true;
      scheduleFlush(flushPending);
    }

    return waiter;
  };

  return { request };
};
